import _ from "lodash";

import { FilterDefinitionType, FilterType } from "@/components/@types";

type FilterOperatorDef = NonNullable<
  NonNullable<FilterDefinitionType["operators"]>[number]
>;

export function flattenFilterOperators(
  operators: FilterDefinitionType["operators"] | undefined,
): FilterOperatorDef[] {
  return _.flatten(operators ?? []);
}

export function getSingleFilterOperator(
  filterDefinition: FilterDefinitionType,
): FilterOperatorDef | null {
  const operators = flattenFilterOperators(filterDefinition.operators);
  if (operators.length !== 1) return null;
  const operator = operators[0];
  return operator?.key ? operator : null;
}

export function normalizeFilterOperator(
  operator: FilterType["operator"] | string | null | undefined,
): FilterType["operator"] | null {
  if (operator == null || operator === "" || operator === "null") {
    return null;
  }
  return operator as FilterType["operator"];
}

export function isFilterOperatorUnset(
  filter: Pick<FilterType, "operator">,
  operators: FilterOperatorDef[],
): boolean {
  const operator = normalizeFilterOperator(filter.operator);
  if (operator == null) {
    return true;
  }
  return !operators.some((op) => op.key === operator);
}

const FALLBACK_OPERATOR_SYMBOLS: Record<string, string> = {
  exact: "=",
  iExact: "=",
  eq: "=",
  ne: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  range: "↔",
  inList: "∈",
  notInList: "∉",
  isNull: "∅",
  isNotNull: "≠∅",
  iContains: "∋",
  contains: "∋",
  startsWith: "^",
  iStartsWith: "^",
  endsWith: "$",
  iEndsWith: "$",
};

/** Normalize operator definition symbols for compact chip display. */
function normalizeOperatorSymbol(symbol: string): string {
  return symbol
    .replace(">=", "≥")
    .replace("<=", "≤")
    .replace("!=", "≠")
    .replace("><", "↔")
    .replace("[?]", "∈")
    .replace("![?]", "∉");
}

/** Symbol shown in compact filter chips instead of the operator name. */
export function getFilterOperatorSymbol(
  operatorKey: FilterType["operator"] | string | null | undefined,
  operators: FilterOperatorDef[],
  operatorName?: string,
): string | null {
  const normalized = normalizeFilterOperator(operatorKey);
  if (normalized == null) {
    return null;
  }

  if (normalized === "isNull" && operatorName === "Is Not Null") {
    return FALLBACK_OPERATOR_SYMBOLS.isNotNull;
  }

  const operator = operators.find((op) => op.key === normalized);
  if (operator?.symbol) {
    return normalizeOperatorSymbol(operator.symbol);
  }

  return FALLBACK_OPERATOR_SYMBOLS[normalized] ?? null;
}

export function getDefaultFilterOperatorState(
  filterDefinition: FilterDefinitionType,
): Pick<FilterType, "operator" | "value"> {
  const operator = getSingleFilterOperator(filterDefinition);
  if (!operator?.key) {
    return { operator: null, value: null };
  }
  return {
    operator: operator.key as FilterType["operator"],
    value: operator.onlyOperator ? true : null,
  };
}

export function createFilterFromDefinition(
  filterKey: string,
  filterDefinition: FilterDefinitionType,
): FilterType {
  const operators = flattenFilterOperators(filterDefinition.operators);
  const singleOperator = getSingleFilterOperator(filterDefinition);
  const defaultOperator = singleOperator?.key ?? operators[0]?.key ?? null;
  return {
    key: filterKey,
    type: filterDefinition.type,
    operator: defaultOperator as FilterType["operator"],
    value: singleOperator?.onlyOperator ? true : null,
  } as FilterType;
}

export const getInputValueForDate = (
  key: string,
  value: {
    date1: string;
    date2: string;
  },
) => {
  return { [key]: value?.date1 };
};
